import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Send, Instagram, Youtube, Twitter } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Kinetic";
import { api } from "@/lib/api";

const TOPICS = ["GENERAL", "BOOKING", "PRESS", "COLLAB", "MERCH ORDER"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "GENERAL", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — the team will get back to you.");
    } catch {
      toast.error("Couldn't send — try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="mx-auto max-w-7xl px-6 pb-40 pt-36">
      <p className="mb-4 font-mono text-xs tracking-[0.4em] text-primary">GET AT US</p>
      <h1 data-testid="contact-title" className="font-anton leading-[0.9]">
        <MaskedLine className="text-6xl text-white md:text-8xl" delay={0.1}>CONTACT</MaskedLine>
        <MaskedLine className="text-outline glow-blue text-6xl md:text-8xl" delay={0.25}>THE SMOKE</MaskedLine>
      </h1>

      <div className="mt-24 grid gap-16 lg:grid-cols-[1fr_1.2fr]">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.4em] text-primary">SOUTHERN SMOKE MUSIC GROUP</p>
          <p className="mt-6 max-w-md leading-relaxed text-white/65">
            Bookings, press, collaborations, merch orders, or just to say the EP knocked — one inbox, real humans, fast replies.
          </p>
          <div className="mt-10 space-y-5 font-mono text-xs tracking-[0.2em]">
            <p className="text-white/50">BOOKINGS & PRESS → <Link to="/label" data-testid="contact-label-link" className="text-primary hover:underline">LABEL PAGE</Link></p>
            <p className="text-white/50">DEMOS → <Link to="/label" data-testid="contact-demo-link" className="text-primary hover:underline">SEND YOUR DEMO</Link></p>
          </div>
          <div className="mt-12 flex items-center gap-5">
            <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="contact-social-instagram" aria-label="Instagram"
              className="border border-white/15 p-3 text-white/70 transition-colors duration-300 hover:border-primary hover:text-primary">
              <Instagram size={18} />
            </a>
            <a href="https://youtube.com" target="_blank" rel="noreferrer" data-testid="contact-social-youtube" aria-label="YouTube"
              className="border border-white/15 p-3 text-white/70 transition-colors duration-300 hover:border-primary hover:text-primary">
              <Youtube size={18} />
            </a>
            <a href="https://twitter.com" target="_blank" rel="noreferrer" data-testid="contact-social-twitter" aria-label="Twitter"
              className="border border-white/15 p-3 text-white/70 transition-colors duration-300 hover:border-primary hover:text-primary">
              <Twitter size={18} />
            </a>
          </div>
          <p data-testid="contact-handle" className="mt-10 font-mono text-xs tracking-[0.35em] text-white/40">@MIKE_MILLZZ_</p>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="border border-white/10 bg-card p-10 md:p-14">
            {sent ? (
              <div data-testid="contact-success" className="flex items-center gap-4 border border-primary/40 bg-primary/10 p-6">
                <span className="bg-primary p-2 text-black"><Check size={18} /></span>
                <p className="font-mono text-xs tracking-[0.2em] text-primary">MESSAGE RECEIVED. WE'LL GET BACK TO YOU.</p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-7">
                <div>
                  <label htmlFor="contact-name" className="font-mono text-[10px] tracking-[0.3em] text-white/50">NAME</label>
                  <input id="contact-name" data-testid="contact-name-input" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-primary" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="font-mono text-[10px] tracking-[0.3em] text-white/50">EMAIL</label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@email.com"
                    className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-primary" />
                </div>
                <div>
                  <label htmlFor="contact-topic" className="font-mono text-[10px] tracking-[0.3em] text-white/50">TOPIC</label>
                  <select id="contact-topic" data-testid="contact-topic-select" value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-white outline-none focus:border-primary">
                    {TOPICS.map((t) => (
                      <option key={t} value={t} className="bg-black">{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="font-mono text-[10px] tracking-[0.3em] text-white/50">MESSAGE</label>
                  <textarea id="contact-message" data-testid="contact-message-input" required rows={4} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="What's on your mind..."
                    className="mt-2 w-full resize-none border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-primary" />
                </div>
                <button type="submit" data-testid="contact-submit-button" disabled={sending}
                  className="flex items-center gap-3 bg-primary px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#fff] disabled:opacity-50">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {sending ? "SENDING..." : "SEND MESSAGE"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </main>
  );
}
